import { useCallback, useEffect, useMemo } from "react";
import {
    useReactTable,
    flexRender,
    getCoreRowModel,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Table as BaseTable } from "reactstrap";
import Loading from "../../../../../../components/Loaders/Loading";

const getRowSpanKeys = (columns) =>
    columns
        .map((c) => c.meta?.rowSpanBy)
        .filter(Boolean);

const sortRowsByRowSpanKeys = (rows, keys) => {
    if (!keys?.length) return rows;

    return [...rows].sort((a, b) => {
        for (const key of keys) {
            const aVal = a.original?.[key] ?? "";
            const bVal = b.original?.[key] ?? "";
            if (aVal !== bVal) {
                return String(aVal).localeCompare(String(bVal));
            }
        }
        return 0;
    });
};

const buildRowSpanMaps = (rows, keys) => {
    const result = {};

    keys.forEach((key) => {
        result[key] = new Map();

        let currentValue = null;
        let groupStartIndex = 0;

        rows.forEach((row, index) => {
            const value = row.original?.[key];

            if (value !== currentValue) {
                // close previous group
                if (index > groupStartIndex) {
                    const span = index - groupStartIndex;
                    result[key].set(rows[groupStartIndex].id, {
                        isFirst: true,
                        span,
                    });
                }

                // start new group
                currentValue = value;
                groupStartIndex = index;
            }

            // default hidden cell
            result[key].set(row.id, {
                isFirst: false,
                span: 0,
            });
        });

        // close last group
        const span = rows?.length - groupStartIndex;
        if (span > 0) {
            result[key].set(rows[groupStartIndex].id, {
                isFirst: true,
                span,
            });
        }
    });

    return result;
};

function Table({
    loading,
    columns,
    data,
    footer = true,
    getRowProps,
    columnVisibility,
    ...props
}) {
    const { t } = useTranslation();

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getRowId: useCallback((row, _, parent) => {
            const id = row.id;
            return parent ? `${parent.id}.${id}` : id;
        }, []),
    });

    const rawRows = table.getRowModel().rows;

    const rowSpanKeys = useMemo(
        () => getRowSpanKeys(columns),
        [columns]
    );

    const sortedRows = useMemo(() => {
        if (!rowSpanKeys?.length) return rawRows;
        return sortRowsByRowSpanKeys(rawRows, rowSpanKeys);
    }, [rawRows, rowSpanKeys]);

    const rowSpanMaps = useMemo(
        () => buildRowSpanMaps(sortedRows, rowSpanKeys),
        [sortedRows, rowSpanKeys]
    );

    useEffect(() => {
        table.setColumnVisibility(columnVisibility);
    }, [columnVisibility, table]);

    return (
        <BaseTable
            className="align-items-center table-flush simple-table mb-0"
            hover
            responsive
            {...props}
        >
            <thead className="bg-secondary">
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                style={{
                                    color: "#000000b5",
                                    borderTop: 0,
                                }}
                            >
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>

            <tbody>
                {loading ? (
                    <tr>
                        <td colSpan="999">
                            <div className="d-flex justify-content-center">
                                <Loading />
                            </div>
                        </td>
                    </tr>
                ) : data?.length === 0 ? (
                    <tr>
                        <td colSpan="999">
                            <div className="d-flex justify-content-center">
                                {t("no-data")}
                            </div>
                        </td>
                    </tr>
                ) : (
                    sortedRows.map((row) => {
                        const rowProps = getRowProps?.(row) || {};

                        return (
                            <tr key={row.id} {...rowProps}>
                                {row.getVisibleCells().map((cell) => {
                                    const key =
                                        cell.column.columnDef.meta?.rowSpanBy;

                                    if (key) {
                                        const info =
                                            rowSpanMaps[key]?.get(row.id);

                                        if (!info?.isFirst) return null;

                                        return (
                                            <td
                                                key={cell.id}
                                                rowSpan={info.span}
                                                className="align-middle text-center"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        );
                                    }

                                    return (
                                        <td key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })
                )}
            </tbody>

            {footer ? (
                <tfoot className="bg-secondary">
                    {table.getFooterGroups().map((footerGroup) => (
                        <tr key={footerGroup.id}>
                            {footerGroup.headers.map((header) => (
                                <td
                                    key={header.id}
                                    colSpan={header.colSpan}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.footer,
                                            header.getContext()
                                        )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tfoot>
            ) : null}
        </BaseTable>
    );
}

export default Table;
